import BmConfirmationDialog from "@/components/dialogs/BmConfirmationDialog.vue";
import type { VueConstructor } from "vue";
import Vue from "vue";
import { BrewManError } from "@/app/errors";
import { useRoot } from "@/components/composables/root";
import BmInformationDialog from "@/components/dialogs/BmInformationDialog.vue";
import type {
  BmConfirmationDialogOptions,
  BmInformationDialogOptions,
} from "@/components/dialogs/DialogTypes";
import type { ComponentPublicInstanceConstructor } from "vue/types/v3-component-public-instance";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import BmUnauthorisedDialog from "@/components/dialogs/BmUnauthorisedDialog.vue";
import type { UnauthorisedComponentProps } from "@/components/pages/UnauthorisedComponent.vue";

export function showConfirmationDialog(
  title: string,
  message: string,
  options?: Partial<BmConfirmationDialogOptions>
): Promise<boolean> {
  return attachAndDisplay(
    "ConfirmationDialog",
    BmConfirmationDialog,
    title,
    message,
    options
  );
}

export function showInformationDialog(
  title: string,
  message: string,
  options?: Partial<BmInformationDialogOptions>
): Promise<boolean> {
  return attachAndDisplay(
    "InformationDialog",
    BmInformationDialog,
    title,
    message,
    options
  );
}

export async function showUnauthorisedDialog(opts: UnauthorisedComponentProps) {
  return await showModalDialog<void, typeof BmUnauthorisedDialog>(
    BmUnauthorisedDialog,
    {
      props: {
        ...opts,
      },
    }
  );
}

function attachAndDisplay(
  componentName: string,
  component: VueConstructor<Vue> | ComponentPublicInstanceConstructor,
  title: string,
  message: string,
  options?: Partial<BmInformationDialogOptions>
) {
  const currentInstance = useRoot();
  let dialogInstance = currentInstance.$children.find(
    (c) => c.$options.name === componentName
  );

  if (!dialogInstance) {
    const confirmationDialogCtor = Vue.extend(component);
    dialogInstance = new confirmationDialogCtor({
      name: componentName,
      parent: currentInstance,
    });
    dialogInstance.$mount();
    currentInstance.$children.push(dialogInstance);
  }

  if (!dialogInstance) {
    throw new BrewManError("Could not attach dialog");
  }

  return (dialogInstance as any).open(title, message, options);
}
